<div class="eligibility-solution-report">
  <PageControl
    :title="'Eligibility Solution Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      @click="show_export_modal"
    >
      <i class="fas fa-download me-1"></i> Export Data
    </button>
  </PageControl>

  <!-- Export Modal - Teleported to body to prevent z-index issues -->
  <Teleport to="body">
    <div
      class="modal fade"
      :id="'export-modal-' + report_reference"
      tabindex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export Eligibility Solution Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-personal-info"
                  v-model="export_options.personal_information"
                />
                <label class="form-check-label" for="export-personal-info">
                  <strong>Personal Information</strong>
                  <small class="d-block text-muted"
                    >Application details, ID number, industry, and overall
                    outcome</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-ivs-rules"
                  v-model="export_options.ivs_rules"
                />
                <label class="form-check-label" for="export-ivs-rules">
                  <strong>IVS Rules</strong>
                  <small class="d-block text-muted"
                    >Identity verification system rules and their
                    outcomes</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-policy-filters"
                  v-model="export_options.policy_filters"
                />
                <label class="form-check-label" for="export-policy-filters">
                  <strong>Policy Filters</strong>
                  <small class="d-block text-muted"
                    >Industry-specific policy filters and their results</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-scorecards"
                  v-model="export_options.scorecards"
                />
                <label class="form-check-label" for="export-scorecards">
                  <strong>Scorecards</strong>
                  <small class="d-block text-muted"
                    >Credit scoring information and risk assessment</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-contact-details"
                  v-model="export_options.contact_details"
                />
                <label class="form-check-label" for="export-contact-details">
                  <strong>Contact Details</strong>
                  <small class="d-block text-muted"
                    >Email addresses, phone numbers, and physical
                    addresses</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-raw-json"
                  v-model="export_options.raw_json"
                />
                <label class="form-check-label" for="export-raw-json">
                  <strong>Raw JSON Data</strong>
                  <small class="d-block text-muted"
                    >Original unprocessed data from the eligibility
                    verification</small
                  >
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <select class="form-select" v-model="export_format">
                <option value="csv">CSV</option>
                <option value="xlsx">Excel (XLSX)</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              :disabled="!has_selected_tables"
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
            >
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-2 header d-flex align-items-center justify-content-between border-bottom mb-2"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h4 fw-bold mb-1">Eligibility Solution Report</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm p-2 bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-id-badge me-2"></i>
            Eligibility Assessment Summary
          </h6>
        </div>
        <div class="card-body">
          <p>
            This report summarizes the eligibility assessment for
            <span class="fw-bold text-dark">{{ full_name }}</span> (ID Number:
            <span class="fw-bold text-dark">{{ id_number }}</span>), who applied
            under the
            <span class="fw-bold text-dark">{{ industry }}</span> industry
            (Application ID:
            <span class="fw-bold text-dark">{{ application_id }}</span>).
          </p>
        </div>
      </div>

      <!-- Report Header -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-user-check me-2"></i>
                Eligibility Verification Report
              </h6>
              <div class="text-muted small">
                Application ID: {{ application_id }} | Generated: {{
                formatted_generated_date }}
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Full Name:</span>
                    <div class="fw-semibold">{{ full_name }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">ID Number:</span>
                    <div class="fw-semibold">{{ id_number }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Industry:</span>
                    <div class="fw-semibold">{{ industry }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern mb-2">Overall Outcome:</span>
                    <div>
                      <span :class="outcome_badge_class" class="status-badge">
                        <i :class="outcome_icon" class="me-1"></i>
                        {{ overall_outcome }}
                      </span>
                    </div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Reason Code:</span>
                    <div class="fw-semibold">{{ reason_code }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Subscriber Code:</span>
                    <div class="fw-semibold">{{ subscriber_code }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-clipboard-check me-2"></i>
                Verification Summary
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Request Status:</span>
                    <div class="fw-semibold">{{ verification_status }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Reason Description:</span>
                    <div class="fw-semibold">{{ reason_description }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Outcome Message:</span>
                    <div class="fw-semibold">{{ outcome_message }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Industry Outcome:</span>
                    <div class="fw-semibold">{{ industry_outcome }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ report_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_generated_date }}
        </div>
      </div>
    </SectionSheet>
    <SectionSheet>
      <!-- IVS Rules Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-shield-alt me-2"></i>
                Identity Verification System (IVS) Rules
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <span class="label-modern">Reason Code:</span>
                    <div class="fw-semibold">{{ ivs_reason_code }}</div>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Rule</th>
                      <th class="text-xs">Description</th>
                      <th class="text-xs">Outcome</th>
                      <th class="text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(rule, rule_key) in ivs_rules_list"
                      :key="rule_key"
                    >
                      <td class="fw-semibold text-xs">{{ rule_key }}</td>
                      <td class="text-xs">{{ rule.Description }}</td>
                      <td class="text-xs">{{ rule.Outcome }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_rule_badge_class(
                            rule.Description === 'Match'
                              ? (rule.Outcome === 'True' ? 'False' : 'True')
                              : rule.Outcome
                          )"
                          class="badge"
                        >
                          <i
                            :class="get_rule_icon(
                              rule.Description === 'Match'
                                ? (rule.Outcome === 'True' ? 'False' : 'True')
                                : rule.Outcome
                            )"
                            class="me-1"
                          ></i>
                          {{ rule.Description === 'Match' ? (rule.Outcome ===
                          'True' ? 'Pass' : 'Fail') : (rule.Outcome === 'True' ?
                          'Fail' : 'Pass') }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Policy Filters Section -->
      <div class="row mb-4" v-if="policy_filters_list && policy_filters_list.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-filter me-2"></i>
                Policy Filters ({{ policy_filter_industry }})
              </h6>
            </div>
            <div class="card-body">
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern mb-2">Overall Outcome:</span>
                    <div>
                      <span
                        :class="get_policy_outcome_badge_class(policy_filter_outcome)"
                        class="status-badge"
                      >
                        <i
                          :class="get_policy_outcome_icon(policy_filter_outcome)"
                          class="me-1"
                        ></i>
                        {{ policy_filter_outcome }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Filter Description</th>
                      <th class="text-xs">Outcome</th>
                      <th class="text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(filter, index) in policy_filters_list"
                      :key="index"
                    >
                      <td class="text-xs">{{ filter.Description }}</td>
                      <td class="text-xs">{{ filter.Outcome }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_rule_badge_class(filter.Outcome)"
                          class="badge"
                        >
                          <i
                            :class="get_rule_icon(filter.Outcome)"
                            class="me-1"
                          ></i>
                          {{ filter.Outcome === 'False' ? 'Pass' : 'Fail' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <!-- No Policy Filters Message -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fas fa-filter me-2"></i>
                  Policy Filters
                </h6>
              </div>
              <div class="card-body">
                <div class="text-center p-4">
                  <i class="fas fa-info-circle fa-3x mb-1 text-info"></i>
                  <h6 class="fw-semibold">No Policy Filters Available</h6>
                    <p class="text-muted mb-0">
                    No industry-specific policy filters are available for this application.
                    Policy filters may not apply if the application did not reach the filtering stage.
                    </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ report_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_generated_date }}
        </div>
      </div>
    </SectionSheet>
    <SectionSheet>
      <!-- Scorecards Section -->
      <div class="row mb-4" v-if="scorecard_score !== 'N/A'">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-chart-line me-2"></i>
                Scorecard Results
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4">
                  <div class="text-center p-4 border rounded">
                    <div class="display-6 fw-bold" :class="score_color_class">
                      {{ scorecard_score }}
                    </div>
                    <div class="text-muted">Credit Score</div>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="mb-3">
                    <span class="label-modern">Scorecard Name:</span>
                    <div class="fw-semibold">{{ scorecard_name }}</div>
                  </div>
                  <div class="mb-3">
                    <span class="label-modern">Score Achieved:</span>
                    <div class="fw-semibold">{{ scorecard_score }}</div>
                  </div>
                  <div class="mb-3">
                    <span class="label-modern">Overall Outcome:</span>
                    <div>
                      <span
                        :class="get_scorecard_outcome_badge_class(scorecard_outcome)"
                        class="status-badge"
                      >
                        <i
                          :class="get_scorecard_outcome_icon(scorecard_outcome)"
                          class="me-1"
                        ></i>
                        {{ scorecard_outcome }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <!-- No Scorecard Results Message -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fas fa-chart-line me-2"></i>
                  Scorecard Results
                </h6>
              </div>
              <div class="card-body">
                <div class="text-center p-4">
                  <i class="fas fa-info-circle text-info fa-3x mb-1"></i>
                  <h6 class="fw-semibold">No Scorecard Results Available</h6>
                  <p class="text-muted mb-0">
                    There are no scorecard results to display for this application.
                    Scorecards may not be applicable or the application may not have reached the scoring stage.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Information Section -->
      <div class="row mb-4" v-if="email_address || home_phone_number || work_telephone || (cell_numbers && cell_numbers.length > 0)">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-address-book me-2"></i>
                Contact Information
              </h6>
            </div>
            <div class="card-body">
              <!-- Email Address -->
              <div class="row mb-3" v-if="email_address">
                <div class="col-12">
                  <span class="label-modern">Email Address:</span>
                  <div class="fw-semibold pt-2 text-xs">
                    {{ email_address }}
                  </div>
                </div>
              </div>
              <!-- Home Telephone -->
              <div class="row mb-3" v-if="home_phone_number">
                <div class="col-12">
                  <span class="label-modern">Home Telephone:</span>
                  <div class="fw-semibold pt-2">
                    {{ home_phone_number.AreaCode }}-{{ home_phone_number.Number }}
                    <span class="text-muted small">
                      ({{ formatDate(home_phone_number.InformationDate) }})
                    </span>
                  </div>
                </div>
              </div>
              <!-- Work Telephone -->
              <div class="row mb-3" v-if="work_telephone">
                <div class="col-12">
                  <span class="label-modern">Work Telephone:</span>
                  <div class="fw-semibold pt-2">
                    {{ work_telephone.AreaCode }}-{{ work_telephone.Number }}
                    <span class="text-muted small">
                      ({{ formatDate(work_telephone.InformationDate) }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <!-- No Contact Information Message -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fas fa-address-book me-2"></i>
                  Contact Information
                </h6>
              </div>
              <div class="card-body">
                <div class="text-center p-4">
                  <i class="fas fa-info-circle text-info fa-3x mb-1"></i>
                  <h6 class="fw-semibold">No Contact Information Available</h6>
                  <p class="text-muted mb-0">
                    There are no contact details to display for this application.
                    Contact information may not have been provided or could not be retrieved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cell Numbers Section -->
      <div class="row mb-4" v-if="cell_numbers && cell_numbers.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-mobile-alt me-2"></i>
                Cell Numbers
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive mt-2">
                <table class="table table-bordered table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Number</th>
                      <th class="text-xs">Information Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(cell, index) in cell_numbers" :key="index">
                      <td class="text-xs">{{ cell.Number }}</td>
                      <td class="text-xs">
                        {{ formatDate(cell.InformationDate) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <!-- No Cell Numbers Message -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fas fa-mobile-alt me-2"></i>
                  Cell Numbers
                </h6>
              </div>
              <div class="card-body">
                <div class="text-center p-4">
                  <i class="fas fa-info-circle text-info fa-3x mb-1"></i>
                  <h6 class="fw-semibold">No Cell Numbers Available</h6>
                  <p class="text-muted mb-0">
                    There are no cell numbers to display for this application.
                    Cell numbers may not have been provided or could not be retrieved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ report_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_generated_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Addresses Section -->
      <div class="row mb-4" v-if="addresses && addresses.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-map-marker-alt me-2"></i>
                Addresses
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive mt-2">
                <table class="table table-bordered table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Address</th>
                      <th class="text-xs">Suburb</th>
                      <th class="text-xs">City</th>
                      <th class="text-xs">Province</th>
                      <th class="text-xs">Postal Code</th>
                      <th class="text-xs">Information Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(address, index) in addresses" :key="index">
                      <td class="text-xs">
                        {{ format_address_line(address) }}
                      </td>
                      <td class="text-xs">{{ address.Suburb || 'N/A' }}</td>
                      <td class="text-xs">{{ address.City || 'N/A' }}</td>
                      <td class="text-xs">{{ address.Province || 'N/A' }}</td>
                      <td class="text-xs">{{ address.PostalCode || 'N/A' }}</td>
                      <td class="text-xs">
                        {{ formatDate(address.InformationDate) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <!-- No Addresses Message -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fas fa-map-marker-alt me-2"></i>
                  Addresses
                </h6>
              </div>
              <div class="card-body">
                <div class="text-center p-4">
                  <i class="fas fa-info-circle text-info fa-3x mb-1"></i>
                  <h6 class="fw-semibold">No Address Information Available</h6>
                  <p class="text-muted mb-0">
                    There are no addresses to display for this application.
                    Address information may not have been provided or could not be retrieved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ report_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_generated_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <div class="card shadow-sm">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Disclaimer
          </h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-info-circle me-2"></i>General Information
            </h6>
            <p class="text-xs mb-4">
              This Eligibility Solution Verification Report (“Report”) is
              prepared solely for the benefit of the intended recipient and is
              based on data obtained from an external source. While The Good
              People Data Company (PTY) LTD (“TGPDC”) employs industry-standard
              practices to collect and process information, TGPDC does not
              guarantee, and expressly disclaims, the accuracy, completeness or
              timeliness of any data contained herein. The Report reflects the
              status of each record at the moment of generation and may not
              reflect subsequent changes.
            </p>
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-shield-lock me-2"></i>Data Privacy
            </h6>
            <p class="text-xs mb-4">
              All personal information are processed in accordance with
              applicable data protection laws including POPIA (Protection of
              Personal Information Act). This report should only be shared with
              authorized personnel who require access to this information for
              legitimate business purposes.
            </p>
            <div class="text-center text-muted mt-3">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company.
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ report_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_generated_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>
